(() => {
  const dialog = document.querySelector('[data-email-popup]');
  if (!dialog || dialog.dataset.initialized || typeof dialog.showModal !== 'function') return;
  dialog.dataset.initialized = 'true';
  const form = dialog.querySelector('[data-email-popup-form]');
  const fields = dialog.querySelector('[data-email-popup-fields]');
  const result = dialog.querySelector('[data-email-popup-result]');
  const error = dialog.querySelector('[data-email-popup-error]');
  const errorDetail = dialog.querySelector('[data-email-popup-error-detail]');
  const pending = dialog.querySelector('[data-email-popup-pending]');
  const submit = form.querySelector('[type="submit"]');
  const submitLabel = submit.querySelector('[data-email-popup-submit-label]');
  const originalLabel = submitLabel.textContent;
  const dismiss = dialog.querySelector('[data-email-popup-dismiss]');
  const dismissLabel = dismiss?.textContent;
  const storageKey = 'harrisons.email-popup.design-v1.dismissed-until';
  const pendingKey = 'harrisons.email-popup.pending';
  const replayHash = '#email-popup-preview';
  const returnHash = '#' + form.id;
  let timer;
  let previousFocus;
  let shown = false;
  let dismissedUntil = 0;
  let pendingAt = 0;
  let attempt = null;
  let protectionPromise;
  let protectionReady = false;
  let protectionRequired = false;
  let resuming = false;
  try { dismissedUntil = Number(localStorage.getItem(storageKey)) || 0; } catch (_) { /* Private browsing still works. */ }
  try { pendingAt = Number(sessionStorage.getItem(pendingKey)) || 0; } catch (_) { /* The return anchor works without storage. */ }
  const serverSuccess = result.dataset.postedSuccessfully === 'true';
  let returned = Boolean((serverSuccess || !error.hidden)
    && (location.hash === returnHash || (pendingAt > Date.now() - 600000 && pendingAt <= Date.now())));

  const clearPendingTimestamp = () => {
    try { sessionStorage.removeItem(pendingKey); } catch (_) { /* No personal data is stored by this script. */ }
  };
  const clearDetails = () => {
    form.querySelectorAll('input[type="email"], input[type="text"], input[type="checkbox"]').forEach(input => {
      if (input.type === 'checkbox') input.checked = false;
      else input.value = '';
    });
  };
  const remember = () => {
    const days = Math.max(1, Number(dialog.dataset.repeatDays) || 7);
    dismissedUntil = Date.now() + days * 86400000;
    try { localStorage.setItem(storageKey, String(dismissedUntil)); } catch (_) { /* Never store an email address. */ }
  };
  const setPending = busy => {
    pending.hidden = !busy;
    fields.setAttribute('aria-busy', String(busy));
    submit.disabled = busy;
    submit.classList.toggle('is-loading', busy);
    submitLabel.textContent = busy ? form.dataset.pendingLabel : originalLabel;
  };
  const finishAttempt = () => {
    if (attempt) clearTimeout(attempt.timeout);
    attempt = null;
    setPending(false);
  };
  const cancelAttempt = () => {
    attempt?.controller.abort();
    finishAttempt();
  };
  const showSuccess = () => {
    fields.hidden = true;
    error.hidden = true;
    result.hidden = false;
    dialog.dataset.response = 'success';
    dialog.setAttribute('aria-labelledby', 'HhEmailPopupResultTitle');
    dialog.setAttribute('aria-describedby', 'HhEmailPopupResultCopy');
    if (dismiss) dismiss.textContent = dismiss.dataset.successLabel;
    clearDetails();
    clearPendingTimestamp();
    remember();
    dialog.scrollTop = 0;
    if (dialog.open) result.focus({ preventScroll: true });
  };
  const showError = message => {
    fields.hidden = false;
    result.hidden = true;
    error.hidden = false;
    errorDetail.textContent = message || form.dataset.requestError;
    clearPendingTimestamp();
    if (dialog.open) error.focus();
  };
  const resetResponse = () => {
    delete dialog.dataset.response;
    dialog.setAttribute('aria-labelledby', 'HhEmailPopupTitle');
    dialog.setAttribute('aria-describedby', 'HhEmailPopupCopy');
    if (dismiss) dismiss.textContent = dismissLabel;
    fields.hidden = false;
    result.hidden = true;
    error.hidden = true;
  };
  if (!returned) resetResponse();
  else {
    clearPendingTimestamp();
    if (serverSuccess) showSuccess();
  }

  const submitBackground = async () => {
    const current = attempt;
    if (!current || current.sent) return;
    current.sent = true;
    try {
      // Keep captured customer input, then collect fresh platform tokens and form_key.
      for (const [name, value] of new FormData(form)) {
        if (!name.startsWith('contact[')) current.payload.set(name, value);
      }
      if (protectionRequired && !current.payload.get('h-captcha-response')) throw new Error('Verification incomplete');
      const action = new URL(form.action, location.href);
      if (action.origin !== location.origin) throw new Error('Unexpected form origin');
      const response = await fetch(action.href, {
        method: 'POST', body: new URLSearchParams(current.payload),
        credentials: 'same-origin', redirect: 'follow', cache: 'no-store',
        headers: { Accept: 'text/html' }, signal: current.controller.signal
      });
      if (attempt !== current) return;
      const responseUrl = new URL(response.url);
      if (responseUrl.origin !== location.origin) throw new Error('Unexpected response origin');
      if (/\/challenge\/?$/.test(responseUrl.pathname)) {
        clearTimeout(current.timeout);
        // Only the platform's actual challenge redirect can navigate the top-level page.
        window.location.assign(responseUrl.href);
        return;
      }
      const html = await response.text();
      if (attempt !== current) return;
      const document = new DOMParser().parseFromString(html, 'text/html');
      const responseForm = document.getElementById(form.id);
      const serverError = responseForm?.querySelector('[data-email-popup-error]:not([hidden])');
      const confirmed = responseForm?.querySelector('[data-email-popup-result][data-posted-successfully="true"]');
      finishAttempt();
      if (serverError) {
        showError(serverError.querySelector('[data-email-popup-error-detail]')?.textContent.trim());
      } else if (response.ok && confirmed) {
        showSuccess();
      } else {
        showError();
      }
    } catch (_) {
      if (attempt !== current) return;
      finishAttempt();
      showError();
    }
  };

  // Install before CAPTCHA binds: Shopify wraps form.submit and calls this transport after verification.
  // Do not touch the underlying CAPTCHA SDK or submit an unverified form.
  const transportInstalled = !form.dataset.hcaptchaBound && !form.dataset.recaptchaBound;
  if (transportInstalled) form.submit = submitBackground;
  const prepareProtection = () => {
    if (protectionPromise) return protectionPromise;
    protectionPromise = new Promise(resolve => {
      if (!transportInstalled) { resolve(false); return; }
      const captcha = window.Shopify?.captcha;
      protectionRequired = typeof captcha?.protect === 'function';
      if (!protectionRequired) {
        protectionReady = true;
        resolve(true);
        return;
      }
      try {
        captcha.protect(form, () => {
          protectionReady = form.submit !== submitBackground;
          resolve(protectionReady);
        });
      } catch (_) { resolve(false); }
    });
    return protectionPromise;
  };

  const isBusy = () => document.hidden
    || document.body.matches('.commerce-surface-open, .nav-open')
    || [...document.querySelectorAll('dialog[open], [aria-modal="true"]:not([hidden]), [data-predictive-results]:not([hidden])')].some(el => el !== dialog && el.getClientRects().length)
    || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
  const isMobile = () => matchMedia('(max-width: 639px)').matches;
  const suppressed = () => dismissedUntil > Date.now()
    || dialog.dataset.subscriber === 'true'
    || (isMobile() ? dialog.dataset.mobile === 'false' : dialog.dataset.desktop === 'false');
  const open = (manual = false) => {
    if (dialog.open || (!manual && (shown || suppressed()))) return;
    if (!returned && isBusy()) {
      clearTimeout(timer);
      timer = setTimeout(() => open(manual), 2000);
      return;
    }
    previousFocus = document.activeElement;
    shown = true;
    dialog.showModal();
    if (dialog.dataset.response !== 'success') prepareProtection();
    const response = returned && (serverSuccess ? result : error);
    (response || dialog.querySelector('[data-email-popup-close]')).focus({ preventScroll: true });
  };
  const close = () => {
    cancelAttempt();
    remember();
    dialog.close();
    if (returned) {
      const cleanUrl = new URL(location.href);
      cleanUrl.searchParams.delete('customer_posted');
      cleanUrl.searchParams.delete('form_type');
      if (cleanUrl.hash === returnHash) cleanUrl.hash = '';
      window.history.replaceState(window.history.state, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
    }
  };
  dialog.querySelectorAll('[data-email-popup-close]').forEach(button => button.addEventListener('click', close));
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('keydown', event => { if (event.key === 'Escape') event.stopPropagation(); });
  dialog.addEventListener('close', () => {
    cancelAttempt();
    clearDetails();
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog || dialog.dataset.backdropClose === 'false') return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  form.addEventListener('submit', event => {
    event.preventDefault();
    if ((attempt && !resuming) || !form.checkValidity()) {
      event.stopImmediatePropagation();
      if (!attempt) form.reportValidity();
      return;
    }
    if (!resuming) {
      const returnInput = form.querySelector('[name="return_to"]');
      if (returnInput) {
        const returnUrl = new URL(location.href);
        returnUrl.searchParams.set('customer_posted', 'true');
        returnUrl.searchParams.delete('form_type');
        returnUrl.hash = returnHash;
        returnInput.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
      }
      try { sessionStorage.setItem(pendingKey, String(Date.now())); } catch (_) { /* The return anchor identifies a challenge response. */ }
      error.hidden = true;
      const current = { payload: new FormData(form), controller: new AbortController(), sent: false };
      attempt = current;
      setPending(true);
      current.timeout = setTimeout(() => {
        if (attempt !== current) return;
        cancelAttempt();
        showError();
      }, 45000);
    }
    if (!protectionReady) {
      event.stopImmediatePropagation();
      const current = attempt;
      prepareProtection().then(ready => {
        if (attempt !== current) return;
        if (!ready) { finishAttempt(); showError(); return; }
        resuming = true;
        try { form.requestSubmit(); }
        catch (_) { finishAttempt(); showError(); }
        finally { resuming = false; }
      });
      return;
    }
    // Leave the verified platform handlers and form-key persistence in the event path.
    // Without CAPTCHA, send after document-level native-form bookkeeping.
    if (!protectionRequired) queueMicrotask(submitBackground);
  }, true);

  const schedule = () => {
    clearTimeout(timer);
    if (returned) { open(true); return; }
    const manual = location.hash === replayHash || (dialog.dataset.editor === 'true' && dialog.dataset.editorPreview === 'true');
    if (!manual && (shown || suppressed() || dialog.dataset.editor === 'true')) return;
    const delay = isMobile() ? dialog.dataset.mobileDelay : dialog.dataset.delay;
    timer = setTimeout(() => open(manual), manual ? 0 : Math.max(0, Number(delay) || 0) * 1000);
  };
  window.addEventListener('hashchange', () => {
    if (location.hash !== replayHash) return;
    cancelAttempt();
    returned = false;
    resetResponse();
    schedule();
  });
  window.addEventListener('pagehide', () => { clearTimeout(timer); cancelAttempt(); });
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
  schedule();
})();
